import { Pagination } from '@src/components/Pagination/Pagination';
import { AdminDataTable } from './AdminDataTable';
import { HelpDeskGift } from './HelpDeskGift';
import { CopyTag } from './CopyTag';
import { HelpDeskActiveStatus } from './HelpDeskActiveStatus';
import { closeDialog, pushDialog } from '@src/components/Dialog/Dialog';
import { HelpDeskSectionTitle } from './HelpDeskSectionTitle';
import { LockIcon } from '@src/components/icons/LockIcon';
import { HelpDeskCardActions } from './HelpDeskCardActions';
import { EyeIcon } from '@src/components/icons/EyeIcon';
import { PlusIcon } from '@src/components/icons/PlusIcon';
import { HelpDeskArchiveBox } from './HelpDeskArchiveBox';
import { HelpDeskDeleteAccount } from './HelpDeskDeleteAccount';
import { HelpDeskRevokePremium } from './HelpDeskRevokePremium';
import { HelpDeskAccessLink, openHelpDeskAccessLink } from './HelpDeskAccessLink';
import { HelpDeskPremium } from './HelpDeskPremium';
import { premiumTierName, type PremiumTier } from '@src/components/PremiumBadge/tierIdentity';
import { premiumFeatures } from '@lub0v/santa-utils';
import { HelpDeskQuestionnaire, type HelpDeskQuestionnaireData } from './HelpDeskQuestionnaire';
import { premiumGameUrl } from '@src/routing/boxDomainNavigation';
import { CloseIcon } from '@src/components/icons/CloseIcon';
import { ChevronDownIcon } from '@src/components/icons/ChevronDownIcon';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Radio, Hint, Spinner, showDialog } from '@src/components';
import { Badge } from '@src/components/Badge/Badge';
import { PremiumBadge } from '@src/components/PremiumBadge/PremiumBadge';
import { UsersIcon } from '@src/components/icons/UsersIcon';
import { CheckIcon } from '@src/components/icons/CheckIcon';
import { ClockIcon } from '@src/components/icons/ClockIcon';
import { KeyIcon } from '@src/components/icons/KeyIcon';
import { ParticipantFace } from '@src/components/faces/ParticipantFace';
import { themePreviewImage } from '@src/theme/previewImages';
import { isSeason, DEFAULT_GAME_SEASON } from '@src/theme/season';
import { loadSeason } from '@src/theme/registry';
import { gamePictureIds } from '@src/theme/gamePictures';
import { useTheme } from '@src/theme/useTheme';
import { CopyValue } from './AdminPayments';
import './AdminHelpDesk.css';

type User = {
  linked_providers?: string[];
  avatar?: string;
  picture?: string;
  boxes: number;
  cards: number;
  id: number;
  username: string | null;
  email: string | null;
  is_verified: boolean;
  last_login_type: string | null;
  last_login_at: string | null;
  active: boolean;
  principal_kind: string;
  last_active_at: string | null;
  recovery_codes_saved_at: string | null;
  is_premium: boolean;
  premium_tier?: string | null;
  created_at: string;
  updated_at: string;
};
type Card = {
  is_ward_known?: boolean;
  giftee_revealed_at?: string | null;
  ward_frame_name?: string | null;
  claim_method?: 'email' | 'link' | 'shared_link' | null;
  claim_recorded_at?: string | null;
  questionnaire?: HelpDeskQuestionnaireData;
  organizer_picture: string | null;
  card_picture: string | null;
  card_avatar: string | null;
  id: number;
  user_id: number | null;
  username: string | null;
  box_id: number;
  ward_id: number | null;
  ward_user_id: number | null;
  ward_name: string | null;
  ward_picture: string | null;
  ward_avatar: string | null;
  random_key: string;
  is_verified: boolean;
  active: boolean;
  created_at: string;
  updated_at: string;
  is_participant: boolean;
  is_admin: boolean;
  organizer_name: string | null;
  access_consumed_at: string | null;
  access_version: number;
  claimed_at: string | null;
  setup_completed_at: string | null;
  box_participants: number;
  box_theme: string | null;
  box_premium: boolean;
  box_premium_key: string | null;
  box_picture: string | null;
  box_logo: string | null;
  box_name: string | null;
  box_key: string | null;
  box_year: string | null;
  box_invite_key: string | null;
  box_active: boolean | null;
  box_draw_conducted: boolean | null;
  box_created_at: string | null;
  box_archived: boolean | null;
};
type Results = {
  users: User[];
  matchedCards: Card[];
  matchedBoxIds?: number[];
  total: number;
  page: number;
  pageSize: number;
};
type Details = {
  user: User;
  cards: Card[];
  total: number;
  boxes: number;
  page: number;
  pageSize: number;
};

function IdTag({ id }: { id: number }) {
  const { t } = useTranslation();
  return <CopyTag value={String(id)} prefix="ID" label={t('admin.helpDesk.copyId')} />;
}

function UserFactIcon({ kind }: { kind: string }) {
  const path = kind === 'created' ? 'M5 5h14v15H5z M8 3v4 M16 3v4 M5 10h14'
    : kind === 'lastLogin' ? 'M13 4h7v16h-7 M3 12h12 M10 7l5 5-5 5'
    : kind === 'lastActive' ? 'M2 12h5l3-8 4 16 3-8h5'
    : 'M20 7v5h-5 M4 17v-5h5 M5 8a7 7 0 0112-3l3 3 M4 16l3 3a7 7 0 0012-3';
  return <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={path} /></svg>;
}
function RecoveryStatus({ saved }: { saved: string | null }) {
  const { t } = useTranslation();
  return <span className="help-desk__recovery-status">
    <svg width="22" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><path d="M12 2c3 3 6 3 8 3v7c0 5-5 8-8 10-3-2-8-5-8-10V5c2 0 5 0 8-3Z" /></svg>
    <span><span>{t('admin.helpDesk.recoverySaved')}</span><span>{t(`admin.helpDesk.${saved ? 'codesSaved' : 'codesNotSaved'}`)}</span>{saved && <span className="help-desk__recovery-date"><CardDate value={saved} /></span>}</span>
  </span>;
}

function loginMethodLabel(method: string | null, ru: boolean) {
  const labels: Record<string, string> = {
    email: 'Email', password: ru ? 'Пароль' : 'Password', vk: 'VK', tg: 'Telegram', google: 'Google', fb: 'Facebook',
    notification: ru ? 'Ссылка из уведомления' : 'Notification link', card_link: ru ? 'Ссылка карточки' : 'Card link',
    recovery_code: ru ? 'Код восстановления' : 'Recovery code', quick_draw: ru ? 'Быстрая жеребьёвка' : 'Quick draw',
  };
  return method ? labels[method] || method : ru ? 'Способ неизвестен' : 'Unknown method';
}

function UserTimestamps({ user }: { user: User }) {
  const { t, i18n } = useTranslation();
  return (<span className="help-desk__timestamps">
                      {(
                        [
                          ['created', user.created_at],
                          ['updated', user.updated_at],
                          ['lastLogin', user.last_login_at],
                          ['lastActive', user.last_active_at],
                        ] as const
                      ).map(([label, value]) => (
                        <span className="help-desk__timestamp-row" key={label}>
                          <UserFactIcon kind={label} />
                          <span>{t(`admin.helpDesk.${label}`)}{label === 'lastLogin' && <small className="help-desk__login-method">{loginMethodLabel(user.last_login_type, i18n.language.startsWith('ru'))}</small>}</span>
                          <CardDate value={value} />
                        </span>
                      ))}
                    </span>);
}


function CardDate({ value }: { value: string | null }) {
  const { i18n } = useTranslation();
  const [now] = useState(() => Date.now());
  if (!value || !Number.isFinite(Date.parse(value))) return <>—</>;
  const instant = new Date(value);
  const seconds = (instant.getTime() - now) / 1000;
  const unit = Math.abs(seconds) < 3600 ? 'minute' : Math.abs(seconds) < 86400 ? 'hour' : 'day';
  const amount = Math.round(seconds / (unit === 'minute' ? 60 : unit === 'hour' ? 3600 : 86400));
  return (
    <>
      <time dateTime={value} title={instant.toLocaleString(i18n.language)}>
        {instant.toLocaleString(i18n.language, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </time>
      <small className="help-desk__relative">
        {new Intl.RelativeTimeFormat(i18n.language, { numeric: 'auto' }).format(amount, unit)}
      </small>
    </>
  );
}

function getCardUrl(card: Card) {
  return card.box_key
    ? new URL(
        `${premiumGameUrl({
          key: card.box_key,
          premiumKey: card.box_premium_key,
          features: premiumFeatures(Boolean(card.box_premium && card.box_active)),
          isArchived: Boolean(card.box_archived),
        }).replace(/\/$/, '')}/card/${encodeURIComponent(card.random_key)}`,
        window.location.origin,
      ).href
    : null;
}
function CardDetails({ card, matched = false }: { card: Card; matched?: boolean }) {
  const { t, i18n } = useTranslation();
  const ru = i18n.language.startsWith('ru');
  const methods = { email: ru ? 'Приглашение по email' : 'Email invitation', link: ru ? 'Ссылка доступа' : 'Access link', shared_link: ru ? 'Общая ссылка игры' : 'Shared game link' };

  return (
    <section className={`help-desk__card${matched ? ' help-desk__card--matched' : ''}`}>
      <div className="help-desk__columns">
        <section className="help-desk__access-summary" aria-label={ru ? 'Доступ к карточке' : 'Card access'}>
          <HelpDeskSectionTitle icon={<LockIcon />}>{ru ? 'Доступ к карточке' : 'Card access'}</HelpDeskSectionTitle>
          <div><KeyIcon /><span>{ru ? 'Способ получения' : 'Claim method'}</span><span>{card.claim_method ? methods[card.claim_method] : ru ? 'Неизвестно' : 'Unknown'}</span></div>
          <div><ClockIcon /><span>{ru ? 'Карточка получена' : 'Card claimed'}</span><span><CardDate value={card.claim_recorded_at || card.claimed_at} /></span></div>
          {!card.is_admin && <>
            <div><CheckIcon /><span>{t('admin.helpDesk.accessConsumed')}</span><span>{card.access_consumed_at ? (ru ? 'Да' : 'Yes') : (ru ? 'Нет' : 'No')}</span></div>
            <div><ClockIcon /><span>{t('admin.helpDesk.accessConsumedAt')}</span><span><CardDate value={card.access_consumed_at} /></span></div>
            <div><span aria-hidden="true">↻</span><span>{t('admin.helpDesk.accessVersion')}</span><span>{card.access_version}</span></div>
          </>}
        </section>
        <section className="help-desk__giftee-summary" aria-label={ru ? 'Подопечный' : 'Giftee'}>
          <HelpDeskSectionTitle icon={<UsersIcon />}>{ru ? 'Подопечный' : 'Giftee'}</HelpDeskSectionTitle>
          {card.ward_id ? <>
            <div className="help-desk__member-identity">
              <span className="help-desk__member-avatar"><ParticipantFace picture={card.ward_picture} avatar={card.ward_avatar} frameName={card.ward_frame_name} index={card.ward_id} size="identity" /></span>
              <strong>{card.ward_name || t('admin.helpDesk.noName')}</strong>
              {card.ward_user_id != null && <CopyTag value={String(card.ward_user_id)} prefix="User ID" label={ru ? 'Скопировать ID пользователя' : 'Copy user ID'} />}
            </div>
            <p className={`help-desk__user-active${card.is_ward_known ? '' : ' help-desk__user-active--deleted'}`}>
              <span className="help-desk__user-active-icon" aria-hidden="true">{card.is_ward_known ? <CheckIcon /> : <CloseIcon />}</span>
              {card.is_ward_known ? (ru ? 'Подопечный просмотрен' : 'Giftee revealed') : (ru ? 'Подопечный ещё не просмотрен' : 'Giftee not yet revealed')}
            </p>
            {card.is_ward_known && <div className="help-desk__giftee-revealed"><ClockIcon /><span>{card.giftee_revealed_at ? <CardDate value={card.giftee_revealed_at} /> : ru ? 'Время просмотра не записано' : 'Reveal time not recorded'}</span></div>}
          </> : <p>{ru ? 'Подопечный пока не назначен' : 'No giftee assigned yet'}</p>}
        </section>
        <HelpDeskQuestionnaire
          data={card.questionnaire}
          participant={card.is_participant}
          completed={Boolean(card.setup_completed_at)}
        />
      </div>
    </section>
  );
}
function GameThumbnail({ game }: { game: Card }) {
  const { theme } = useTheme();
  const season = isSeason(game.box_theme) ? game.box_theme : DEFAULT_GAME_SEASON;
  const [icon, setIcon] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Reset the selected server resource before loading its latest data.
    setIcon(null);
    void loadSeason(season)
      .then((pkg) => {
        const id = gamePictureIds.find((id) => id === game.box_picture) ?? 'santa';
        if (active)
          setIcon(game.box_picture === 'custom_image' ? pkg.photoArtwork : pkg.gamePictures[id]);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [season, game.box_picture]);
  return (
    <span className="help-desk__game-thumbnail" aria-hidden="true">
      <img className="help-desk__game-scene" src={themePreviewImage(season, theme)} alt="" />
      {(game.box_logo || icon) && (
        <img className="help-desk__game-icon" src={game.box_logo || icon || ''} alt="" />
      )}
    </span>
  );
}
function TransferCardProfile({ card, destination }: { card: Card; destination?: number | null }) {
  const { i18n } = useTranslation();
  const ru = i18n.language.startsWith('ru');
  return <span className="help-desk__transfer-card-profile">
              <span className="help-desk__merge-avatar" aria-hidden="true">{card.card_avatar ? <img src={card.card_avatar} alt="" /> : <ParticipantFace picture={card.card_picture || undefined} index={card.id} size="identity" />}</span>
              <span className="help-desk__merge-info">
                <span className="help-desk__merge-name"><strong>{card.username || (ru ? 'Без имени' : 'No name')}</strong><CopyTag prefix="ID" value={String(card.id)} displayText={`${ru ? 'Карточка' : 'Card'} #${card.id}`} label={ru ? 'Скопировать ID карточки' : 'Copy card ID'} /><span>{card.box_name}</span></span>
                <span><CopyTag prefix="User ID" value={String(card.user_id)} displayText={`User ID: ${card.user_id}`} label={ru ? 'Скопировать ID пользователя' : 'Copy user ID'} /></span>
                <span className="help-desk__merge-meta"><HelpDeskActiveStatus active={card.active}>{card.active ? (ru ? 'Активна' : 'Active') : (ru ? 'Удалена' : 'Deleted')}</HelpDeskActiveStatus>{card.user_id === destination ? (ru ? ' · Уже у выбранного пользователя' : ' · Already belongs to destination') : ''}</span>
              </span>
            </span>;
}

function TransferUserProfile({ user }: { user: User }) {
  const { i18n } = useTranslation();
  const ru = i18n.language.startsWith('ru');
  return <span className="help-desk__transfer-card-profile">        <span className="help-desk__merge-avatar" aria-hidden="true">
          {user.avatar ? <img src={user.avatar} alt="" /> : <ParticipantFace picture={user.picture} index={user.id} size="identity" />}
        </span>
        <span className="help-desk__merge-info">
          <span className="help-desk__merge-name"><strong>{user.username || (ru ? 'Без имени' : 'No name')}</strong><CopyTag prefix="User ID" value={String(user.id)} displayText={`User ID: ${user.id}`} label={ru ? 'Скопировать ID пользователя' : 'Copy user ID'} /></span>
          <span>{user.email || (ru ? 'Email не указан' : 'No email')}</span>
          <span className="help-desk__merge-meta">
            <HelpDeskActiveStatus active={user.active}>{user.active ? (ru ? 'Активен' : 'Active') : (ru ? 'Неактивен' : 'Inactive')}</HelpDeskActiveStatus>
            <span>{ru ? 'Соцсети: ' : 'Socials: '}{user.linked_providers == null ? (ru ? 'неизвестно' : 'unknown') : user.linked_providers.length ? user.linked_providers.map(provider => ({ vk: 'VK', tg: 'Telegram', google: 'Google' })[provider] || provider).join(', ') : (ru ? 'не привязаны' : 'none linked')}</span>
          </span>
        </span></span>;
}

function MergeAccountSelection({ users, onChanged }: { users: User[]; onChanged: () => void }) {
  const { i18n } = useTranslation();
  const ru = i18n.language.startsWith('ru');
  const [destination, setDestination] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState<number | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [chosenCards, setChosenCards] = useState<number[]>([]);
  const [loadingCards, setLoadingCards] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [retryCards, setRetryCards] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Reset the selected server resource before loading its latest data.
    setLoadingCards(true); setLoadFailed(false);
    void Promise.all(users.map(async user => {
      const loaded: Card[] = [];
      let page = 1;
      while (true) {
        const response = await fetch(`/api/admin/help-desk/users/${user.id}?page=${page}`, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('Cards unavailable');
        const data: Details = await response.json();
        if (!Array.isArray(data.cards) || !Number.isFinite(data.total) || data.pageSize <= 0) throw new Error('Invalid cards response');
        loaded.push(...data.cards);
        if (page * data.pageSize >= data.total) break;
        page++;
      }
      return loaded;
    })).then(groups => {
      if (!controller.signal.aborted) setCards(Array.from(new Map(groups.flat().map(card => [card.id, card])).values()));
    }).catch(() => { if (!controller.signal.aborted) setLoadFailed(true); })
      .finally(() => { if (!controller.signal.aborted) setLoadingCards(false); });
    return () => controller.abort();
  }, [users, retryCards]);
  const moveCount = cards.filter(card => chosenCards.includes(card.id) && card.user_id !== destination).length;

  async function transfer() {
    if (destination == null || busy || loadingCards || loadFailed || !moveCount) return;
    setBusy(true); setError('');
    try {
      const response = await fetch('/api/admin/help-desk/transfer-cards', {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userIds: users.map(user => user.id), destinationId: destination, cards: cards.filter(card => chosenCards.includes(card.id)).map(card => ({ cardId: card.id, userId: card.user_id })) }),
      });
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message.includes('TRANSFER_USER_ALREADY_IN_GAME')
          ? (ru ? 'Выбранные карточки создадут повторное участие в одной игре. Передача отменена: ни одна карточка не перемещена.' : 'Selected cards would create duplicate participation in a game. No cards were moved.')
          : message.includes('TRANSFER_CARD_CHANGED') ? (ru ? 'Владелец карточки уже изменился. Закройте окно и выберите карточки заново.' : 'A card owner has changed. Close this dialog and select the cards again.')
          : (ru ? 'Не удалось передать карточки. Обновите данные и попробуйте снова.' : 'Could not transfer cards. Refresh the data and try again.'));
      }
      const data = await response.json();
      setDone(data.transferredCards); onChanged();
      if (data.transferredCards > 0) {
        const target = users.find(user => user.id === destination);
        const moved = cards.filter(card => chosenCards.includes(card.id) && card.user_id !== destination);
        const describeCard = (card: Card) => ({ name: card.username || '', box: card.box_name || '' });
        openHelpDeskAccessLink(destination, ru, {
          destination: target?.username ? `${target.username}${target.email ? ` (${target.email})` : ''}` : target?.email || (ru ? 'выбранный аккаунт' : 'selected account'),
          transferredCards: moved.map(describeCard),
          destinationCards: cards.filter(card => card.user_id === destination || moved.some(value => value.id === card.id)).map(describeCard),
        });
      }
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setBusy(false); }
  }

  function confirmTransfer() {
    if (destination == null || !moveCount || busy) return;
    const target = users.find(user => user.id === destination)!;
    const moving = cards.filter(card => chosenCards.includes(card.id) && card.user_id !== destination);
    pushDialog({
      title: ru ? 'Подтвердить передачу карточек?' : 'Confirm card transfer?',
      closeLabel: ru ? 'Закрыть' : 'Close',
      testId: 'help-desk-transfer-confirm',
      children: <div className="help-desk__transfer-confirm">
        <p>{ru ? 'Эти карточки перейдут выбранному пользователю:' : 'These cards will belong to the selected user:'}</p>
        <div className="help-desk__merge-options">
          {moving.map(card => <div key={card.id} className="help-desk__transfer-card"><TransferCardProfile card={card} /></div>)}
        </div>
        <p className="help-desk__merge-note">{ru ? 'Получатель карточек' : 'Destination user'}</p>
        <div className="help-desk__transfer-card"><TransferUserProfile user={target} /></div>
        <p>{ru ? 'После передачи будет создана одноразовая ссылка для входа в этот аккаунт. Сообщение не отправляется автоматически.' : 'After transfer, a one-time sign-in link will be created for this account. The message is not sent automatically.'}</p>
        <div className="help-desk__merge-actions">
          <Button variant="secondary" onClick={() => closeDialog()}>{ru ? 'Отмена' : 'Cancel'}</Button>
          <Button onClick={() => { closeDialog(); void transfer(); }}>{ru ? 'Да, передать карточки' : 'Yes, transfer cards'}</Button>
        </div>
      </div>,
    });
  }

  return <div className="help-desk__merge">
    <fieldset className="help-desk__merge-options help-desk__transfer-cards" disabled={busy || done !== null || loadingCards}>
      <legend>{ru ? '1. Выберите карточки для передачи' : '1. Select cards to transfer'}</legend>
      {loadingCards ? <Spinner label={ru ? 'Загрузка карточек' : 'Loading cards'} /> : loadFailed
        ? <Hint tone="error">{ru ? 'Не удалось загрузить карточки.' : 'Could not load cards.'} <Button variant="ghost" onClick={() => setRetryCards(value => value + 1)}>{ru ? 'Повторить' : 'Retry'}</Button></Hint>
        : cards.length === 0 ? <p>{ru ? 'У выбранных пользователей нет карточек.' : 'These users have no cards.'}</p>
        : cards.map(card => <div key={card.id} className={`help-desk__transfer-card${chosenCards.includes(card.id) ? ' help-desk__merge-option--selected' : ''}`}>
          <Checkbox checked={chosenCards.includes(card.id)}
            aria-label={`${ru ? 'Карточка' : 'Card'} #${card.id} · ${card.username || '—'}`}
            onCheckedChange={checked => { setError(''); setChosenCards(current => checked ? [...current, card.id] : current.filter(id => id !== card.id)); }}
            label={<TransferCardProfile card={card} destination={destination} />} />
        </div>)}
    </fieldset>
    <fieldset className="help-desk__merge-options" disabled={busy || done !== null}>
      <legend>{ru ? '2. Кому передать выбранные карточки?' : '2. Who should own the selected cards?'}</legend>
      {users.map(user => <label key={user.id} className={`help-desk__merge-option${destination === user.id ? ' help-desk__merge-option--selected' : ''}`}>
        <Radio indicator name="merge-destination" value={user.id} checked={destination === user.id}
          aria-label={`${user.username || (ru ? 'Без имени' : 'No name')} · User ID: ${user.id}`}
          onCheckedChange={() => { setDestination(user.id); setError(''); }} />
        <TransferUserProfile user={user} />
      </label>)}
    </fieldset>
    <p className="help-desk__merge-note" role="status">{destination == null
      ? (ru ? 'Выберите аккаунт, который хотите оставить основным.' : 'Select the account you want to keep as the destination.')
      : <>{ru ? 'Основной аккаунт: ' : 'Destination account: '}<CopyTag prefix="User ID" value={String(destination)} displayText={`#${destination}`} label={ru ? 'Скопировать ID пользователя' : 'Copy user ID'} /></>}</p>
    <p className="help-desk__merge-note">{ru ? 'Только выбранные карточки перейдут указанному пользователю. Email, соцсети и данные аккаунтов сохранятся. Аккаунты останутся активны, если были активны до передачи.' : 'Only selected cards will move to the destination account. Emails, socials, account details and account active statuses will remain unchanged.'}</p>
    {error && <Hint tone="error">{error}</Hint>}
    <div className="help-desk__merge-actions">{done !== null ? <p role="status" className="help-desk__merge-note">{ru ? `Передано карточек: ${done}.` : `Cards transferred: ${done}.`}</p>
      : <Button disabled={destination == null || busy || loadingCards || loadFailed || !moveCount} onClick={confirmTransfer}>{busy ? (ru ? 'Передача…' : 'Transferring…') : (ru ? `Передать карточки (${moveCount})` : `Transfer cards (${moveCount})`)}</Button>}
    <Button variant="ghost" disabled={busy} onClick={() => closeDialog()}>{ru ? 'Закрыть' : 'Close'}</Button></div>
  </div>;
}

function SocialAccounts({ user }: { user: User }) {
  const { i18n } = useTranslation();
  const ru = i18n.language.startsWith('ru');
  return <section className="help-desk__social-column" aria-label={ru ? 'Социальные сети' : 'Social networks'}>
    <h3>{ru ? 'Социальные сети' : 'Social networks'}</h3>
    {([['vk', 'VK'], ['tg', 'Telegram'], ['google', 'Google']] as const).map(([provider, name]) => {
      const linked = user.linked_providers?.includes(provider);
      return <div className="help-desk__social-row" key={provider}>
        <span>{name}</span>
        <span className={linked ? 'help-desk__social-linked' : ''}>
          {user.linked_providers == null ? '—' : linked ? (ru ? 'Привязан' : 'Linked') : (ru ? 'Не привязан' : 'Not linked')}
        </span>
        <small>{user.last_login_type === provider && user.last_login_at ? <>{ru ? 'Вход: ' : 'Login: '}<CardDate value={user.last_login_at} /></> : linked ? (ru ? 'Время входа не записано' : 'Login time not recorded') : null}</small>
      </div>;
    })}
  </section>;
}

function CardDialogTitle({ card }: { card: Card }) {
  const { id, box_name: boxName } = card;
  const cardUrl = getCardUrl(card);
  const { i18n } = useTranslation();
  const ru = i18n.language.startsWith('ru');
  return <span className="help-desk__card-dialog-title">{ru ? 'Карточка' : 'Card'} <CopyValue value={String(id)} displayText={`#${id}`} label={ru ? 'Скопировать ID карточки' : 'Copy card ID'} />{boxName && <span>· {boxName}</span>}                {cardUrl && (
                  <a
                    className="help-desk__open-game"
                    href={cardUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {ru ? 'Открыть карточку' : 'Open card'}
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path d="M7 17 17 7M7 7h10v10" />
                    </svg>
                  </a>
                )}</span>;
}

function CardDialog({ id, onDenied, onChanged }: { id: number; onDenied: () => void; onChanged?: () => void }) {
  const { t, i18n } = useTranslation();
  const ru = i18n.language.startsWith('ru');
  const [data, setData] = useState<{ card: Card; user: User | null } | null>(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Reset the selected server resource before loading its latest data.
    setError(false);
    void fetch(`/api/admin/help-desk/cards/${id}`, { signal: controller.signal, credentials: 'same-origin', cache: 'no-store' })
      .then(async response => {
        if ([401, 403].includes(response.status)) { onDenied(); throw new Error('Denied'); }
        if (!response.ok) throw new Error('Unavailable');
        const result = await response.json();
        if (!result.card) throw new Error('Invalid card');
        if (!controller.signal.aborted) setData(result);
      }).catch(() => { if (!controller.signal.aborted) setError(true); });
    return () => controller.abort();
  }, [id, retry, onDenied]);
  return <div className="help-desk help-desk__card-dialog">
    {error ? <Hint tone="error">{t('admin.helpDesk.error')} <Button onClick={() => setRetry(value => value + 1)}>{t('admin.helpDesk.retry')}</Button></Hint>
      : !data ? <Spinner label={t('account.loading')} /> : <>
        <div className="help-desk__card-statuses" aria-label={ru ? 'Статус карточки' : 'Card status'}>
          <HelpDeskActiveStatus active={data.card.active}>{data.card.active ? (ru ? 'Карточка активна' : 'Card active') : (ru ? 'Карточка удалена' : 'Card deleted')}</HelpDeskActiveStatus>
          {data.card.claimed_at ? (
            <span className="help-desk__user-active">
              <span className="help-desk__user-active-icon" aria-hidden="true"><CheckIcon /></span>
              {t('admin.helpDesk.cardOpened')}
            </span>
          ) : (
            <Badge tone="warning" className="help-desk__draw-pending">{t('admin.helpDesk.cardNotOpened')}</Badge>
          )}
        </div>
        {data.user ? <section className="help-desk__dialog-profile">
          <span className="help-desk__avatar">
            {data.user.avatar ? <img src={data.user.avatar} alt="" /> : <ParticipantFace picture={data.user.picture || data.card.card_picture} avatar={data.card.card_avatar} index={data.user.id} size="identity" />}
          </span>
          <span className="help-desk__names">
            <span className="help-desk__name-id">
              <CopyTag value={String(data.user.id)} prefix="User ID" label={ru ? 'Скопировать ID пользователя' : 'Copy user ID'} />
              <strong className="help-desk__username">{data.user.username || t('admin.helpDesk.noName')}</strong>
            </span>
            {data.user.active ? <span className="help-desk__user-active"><span className="help-desk__user-active-icon" aria-hidden="true"><CheckIcon /></span>{ru ? 'Пользователь активен' : 'User active'}</span> : <Badge className="help-desk__user-status help-desk__user-status--deleted">{t('admin.helpDesk.deleted')}</Badge>}
            <span className={`help-desk__email help-desk__email--${data.user.is_verified ? 'verified' : 'unverified'}`}>{data.user.email ? <CopyValue value={data.user.email} label={t('admin.helpDesk.copyEmail')} /> : '—'}</span>
          </span>
          <UserTimestamps user={data.user} />
          <span className="help-desk__recovery-column"><RecoveryStatus saved={data.user.recovery_codes_saved_at} /></span>
        </section> : <p>{ru ? 'Карточка не привязана к профилю.' : 'This card has no profile.'}</p>}
        <CardDetails card={data.card} />
        <HelpDeskCardActions card={data.card} onDenied={onDenied} onChanged={() => { setRetry(value => value + 1); onChanged?.(); }} />
      </>}
  </div>;
}

type RosterActions = { addUser: (id: number) => void; userIds: number[]; busy: boolean; refresh: () => void };

type BoxMember = {
  is_ward_known?: boolean;
  giftee_revealed_at?: string | null;
  ward_user_id: number | null;
  card_frame_name: string | null; ward_frame_name: string | null;
  ward_picture: string | null; ward_avatar: string | null; ward_is_admin: boolean | null;
  ward_id: number | null; ward_name: string | null; created_at: string | null;
  id: number; user_id: number | null; username: string | null; random_key: string;
  active: boolean; is_admin: boolean; is_participant: boolean;
  card_picture: string | null; card_avatar: string | null; user_exists: boolean;
  last_active_at: string | null; recovery_codes_saved_at: string | null;
};

function BoxMembers({ boxId, game, currentUserId, onDenied, rosterActions }: { boxId: number; game: Card; currentUserId: number; onDenied: () => void; rosterActions: RosterActions }) {
  const { t, i18n } = useTranslation();
  const ru = i18n.language.startsWith('ru');
  const [page, setPage] = useState(1);
  const [retry, setRetry] = useState(0);
  const [error, setError] = useState(false);
  const [data, setData] = useState<{ members: BoxMember[]; total: number; page: number; pageSize: number } | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Reset the selected server resource before loading its latest data.
    setData(null);
    setError(false);
    void fetch(`/api/admin/help-desk/boxes/${boxId}/members?page=${page}`, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal })
      .then(async response => {
        if ([401, 403].includes(response.status)) { onDenied(); return; }
        if (!response.ok) throw new Error('Unavailable');
        const result = await response.json();
        if (!Array.isArray(result.members)) throw new Error('Invalid roster');
        if (!controller.signal.aborted) setData(result);
      }).catch(() => { if (!controller.signal.aborted) setError(true); });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxId, page, retry]);
  return <section className="help-desk__members" aria-label={ru ? 'Пользователи игры' : 'Game users'}>
    <h3>{ru ? 'Пользователи игры' : 'Game users'}{data && ` · ${data.total}`}</h3>
    {error ? <Hint tone="error">{t('admin.helpDesk.error')} <Button size="small" onClick={() => setRetry(value => value + 1)}>{t('admin.helpDesk.retry')}</Button></Hint>
      : !data ? <Spinner label={t('account.loading')} /> : <>
        {!data.members.length && <p>{ru ? 'В игре нет карточек.' : 'No cards in this game.'}</p>}
        <div className="help-desk__member-scroll" tabIndex={0} role="region" aria-label={ru ? 'Таблица пользователей' : 'Users table'}>
        <AdminDataTable pagination={{ page: data.page, pageCount: Math.max(1, Math.ceil(data.total / data.pageSize)), onChange: setPage, previousLabel: t('admin.payments.previous'), nextLabel: t('admin.payments.next'), pageLabel: value => t('admin.payments.page', { page: value }) }} className="help-desk__member-table">
          <thead><tr><th scope="col">User ID</th><th scope="col">{ru ? 'Имя пользователя' : 'User name'}</th><th scope="col">{ru ? 'Подопечный' : 'Giftee'}</th><th scope="col">{ru ? 'Последняя активность' : 'Last activity'}</th><th scope="col">{ru ? 'Коды' : 'Codes'}</th><th scope="col">{ru ? 'Карточка создана' : 'Card created'}</th><th scope="col">{ru ? 'Действия' : 'Actions'}</th></tr></thead>
          <tbody>
          {data.members.map(member => <tr className={`help-desk__member${member.user_id === currentUserId ? ' help-desk__member--current' : ''}`} key={member.id} aria-current={member.user_id === currentUserId ? 'true' : undefined}>
            <td><span className="help-desk__member-identity">
            <span className={`help-desk__user-active-icon help-desk__member-status help-desk__member-status--${member.active ? 'active' : 'deleted'}`}
              role="img" aria-label={ru ? (member.active ? 'Карточка активна' : 'Карточка удалена') : (member.active ? 'Active card' : 'Deleted card')}
              title={ru ? (member.active ? 'Карточка активна' : 'Карточка удалена') : (member.active ? 'Active card' : 'Deleted card')}>
              {member.active ? <CheckIcon /> : <CloseIcon />}
            </span>
            <span className="help-desk__member-id">{member.user_id != null ? <CopyTag value={String(member.user_id)} prefix="User ID" label={ru ? 'Скопировать ID пользователя' : 'Copy user ID'} /> : <span>{ru ? 'Без профиля' : 'No profile'}</span>}</span>
            </span></td>
            <td><span className="help-desk__member-identity"><span className="help-desk__member-avatar">
              <ParticipantFace frameName={member.card_frame_name} picture={member.card_picture || undefined} avatar={member.card_avatar || undefined} index={member.id} size="identity" />
            </span>
            <span className="help-desk__member-name"><strong>{member.username || t('admin.helpDesk.noName')}</strong>
              {member.is_admin && <small>{t('admin.helpDesk.organizer')}</small>}{member.user_id === currentUserId && <span className="help-desk__member-current-label">{ru ? 'Текущий пользователь' : 'Current user'}</span>}</span></span></td>
            <td>{member.ward_id != null ? <span className="help-desk__member-identity"><span className="help-desk__member-avatar">
              <ParticipantFace frameName={member.ward_frame_name} picture={member.ward_picture || undefined} avatar={member.ward_avatar || undefined} index={member.ward_id} size="identity" />
            </span><span className="help-desk__member-name"><strong>{member.ward_name || t('admin.helpDesk.noName')}</strong>{member.ward_is_admin && <small>{t('admin.helpDesk.organizer')}</small>}{member.ward_user_id === currentUserId && <span className="help-desk__member-current-label">{ru ? 'Текущий пользователь' : 'Current user'}</span>}
              <span className={`help-desk__roster-reveal${member.is_ward_known ? ' help-desk__roster-reveal--seen' : ''}`}>
                {member.is_ward_known ? <CheckIcon /> : <EyeIcon />}
                {member.is_ward_known ? (ru ? 'Просмотрен' : 'Revealed') : (ru ? 'Не просмотрен' : 'Not revealed')}
              </span>
              {member.is_ward_known && <small className="help-desk__roster-reveal-time">{member.giftee_revealed_at ? <CardDate value={member.giftee_revealed_at} /> : ru ? 'Время не записано' : 'Time not recorded'}</small>}
            </span></span> : '—'}</td>
            <td><span className="help-desk__member-activity"><ClockIcon /><CardDate value={member.last_active_at} /></span></td>
            <td className="help-desk__member-meta">
              {!member.user_exists ? '—' : member.recovery_codes_saved_at ? <span title={new Date(member.recovery_codes_saved_at).toLocaleString(i18n.language)}>{ru ? 'Сохранены' : 'Saved'}</span> : <span>{ru ? 'Не сохранены' : 'Not saved'}</span>}</td>
            <td><span className="help-desk__member-activity"><ClockIcon /><CardDate value={member.created_at} /></span></td>
            <td><span className="help-desk__member-actions"><Button variant="ghost" className="help-desk__action-button" icon={<EyeIcon />}
              aria-label={ru ? `Посмотреть карточку #${member.id}` : `View card #${member.id}`}
              title={ru ? 'Посмотреть карточку' : 'View card'}
              onClick={() => showDialog({ title: <CardDialogTitle card={{ ...game, id: member.id, random_key: member.random_key }} />, mobileFullScreen: true, closeLabel: ru ? 'Закрыть' : 'Close', testId: 'help-desk-card-dialog', children: <CardDialog id={member.id} onDenied={onDenied} onChanged={() => { setRetry(value => value + 1); rosterActions.refresh(); }} /> })} />
            <Button variant="ghost" className="help-desk__action-button" icon={<PlusIcon />}
              disabled={member.user_id == null || !member.user_exists || rosterActions.busy || rosterActions.userIds.includes(member.user_id)}
              aria-label={ru ? `Добавить пользователя #${member.user_id ?? '—'} в результаты` : `Add user #${member.user_id ?? '—'} to results`}
              title={member.user_id != null && rosterActions.userIds.includes(member.user_id) ? (ru ? 'Уже в результатах' : 'Already in results') : (ru ? 'Добавить в результаты' : 'Add to results')}
              onClick={() => { if (member.user_id != null) rosterActions.addUser(member.user_id); }} /></span></td>
          </tr>)}
          </tbody>
        </AdminDataTable></div>

      </>}
  </section>;
}

function GameRow({
  rosterActions,
  currentUserId,
  cards,
  matchedIds,
  matchedBoxIds,
  onRevoked,
  onDenied,
}: {
  rosterActions: RosterActions;
  currentUserId: number;
  onRevoked: () => void;
  onDenied: () => void;
  cards: Card[];
  matchedIds: number[];
  matchedBoxIds: number[];
}) {
  const { t, i18n } = useTranslation();
  const game = cards[0];
  const [open, setOpen] = useState(matchedBoxIds.includes(game.box_id) || cards.some(card => matchedIds.includes(card.id)));
  const inviteUrl =
    game.box_key && game.box_invite_key
      ? (() => {
          const path = premiumGameUrl({
            key: game.box_key,
            premiumKey: game.box_premium_key,
            features: premiumFeatures(Boolean(game.box_premium && game.box_active)),
            isArchived: Boolean(game.box_archived),
          });
          const url = new URL(path, window.location.origin);
          url.searchParams.set('join', game.box_invite_key);
          return url.pathname === '/' ? `${url.origin}${url.search}` : url.href;
        })()
      : null;
  const organizers = cards.filter((card) => card.is_admin);
  const participants = cards.filter((card) => card.is_participant);
  const status = game.box_active === false ? 'deleted' : game.box_archived ? 'archived' : 'active';
  return (
    <details
      className="help-desk__game"
      open={open}
      onToggle={event => setOpen(event.currentTarget.open)}
    >
      <summary className="help-desk__game-summary">
        <GameThumbnail game={game} />
        <span className="help-desk__game-name">
          <span className="help-desk__game-title">
            {game.box_active != null && (
              <span
                className={`help-desk__active-dot help-desk__status-dot--${status}`}
                role="img"
                aria-label={t(`admin.helpDesk.${status}`)}
                title={t(`admin.helpDesk.${status}`)}
              />
            )}
            {(status !== 'active' || game.box_active == null) && (
              <Badge className={`help-desk__game-status help-desk__game-status--${status}`}>
                {game.box_active == null ? '—' : t(`admin.helpDesk.${status}`)}
              </Badge>
            )}
            <span className="help-desk__game-id" onClick={(event) => event.stopPropagation()}>
              <IdTag id={game.box_id} />
            </span>
            {game.box_key && (
              <CopyTag value={game.box_key} prefix="Key"
                label={t('admin.helpDesk.copyBoxKey')} />
            )}
            <strong>{game.box_name || t('admin.helpDesk.missingBox')}</strong>
            {game.box_draw_conducted != null && (
              <Badge
                tone={game.box_draw_conducted ? 'info' : 'warning'}
                className={game.box_draw_conducted ? undefined : 'help-desk__draw-pending'}
              >
                {t(
                  `admin.helpDesk.${
                    game.box_draw_conducted ? 'drawConducted' : 'drawNotConducted'
                  }`,
                )}
              </Badge>
            )}
          </span>
          <span
            className="help-desk__game-meta help-desk__game-keys"
            onClick={(event) => event.stopPropagation()}
          >
            <span className="help-desk__created-inline">
              <strong>{t('admin.helpDesk.created')}:</strong>{' '}
              <CardDate value={game.box_created_at} />
            </span>
            {inviteUrl && (
              <a className="help-desk__open-game" href={inviteUrl} target="_blank" rel="noreferrer">
                {t('admin.helpDesk.openLink')}
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M7 17 17 7M7 7h10v10" />
                </svg>
              </a>
            )}
          </span>
          <span className="help-desk__game-roles">
            <span className="help-desk__role-badges">
              {organizers.length > 0 && <Badge tone="info">{t('admin.helpDesk.organizer')}</Badge>}
              {participants.length > 0 && <Badge>{t('admin.helpDesk.participant')}</Badge>}
            </span>
            {participants.length > 0 && (
              <span className="help-desk__participant-name">
                {[
                  ...new Set(
                    participants.map((card) => card.username || t('admin.helpDesk.noName')),
                  ),
                ].join(', ')}
              </span>
            )}
            <span className="help-desk__box-count">
              <UsersIcon />
              {t('admin.helpDesk.boxParticipants')}: <strong>{game.box_participants}</strong>
            </span>
          </span>
        </span>
        <span className="help-desk__game-end">
          {game.box_premium && <PremiumBadge>{t('admin.helpDesk.premium')}</PremiumBadge>}
          <span className="help-desk__box-header-actions" role="group"
            aria-label={i18n.language.startsWith('ru') ? 'Действия с игрой' : 'Game actions'}
            onClick={event => event.stopPropagation()}>
            <HelpDeskArchiveBox
              action={game.box_active === false ? 'permanent' : game.box_archived ? 'delete' : 'archive'}
              id={game.box_id}
              name={game.box_name || String(game.box_id)}
              onDenied={onDenied}
              onArchived={onRevoked}
            />
            {game.box_premium && <HelpDeskRevokePremium resource="boxes" id={game.box_id} onDenied={onDenied} onRevoked={onRevoked} />}
          </span>
          <span className="help-desk__game-arrow" aria-hidden="true">
            <ChevronDownIcon />
          </span>
        </span>
      </summary>
      <div className="help-desk__game-content">
        {open && <BoxMembers game={game} rosterActions={rosterActions} currentUserId={currentUserId} boxId={game.box_id} onDenied={onDenied} />}
      </div>
    </details>
  );
}

function UserDetails({
  revision,
  rosterActions,
  id,
  matchedIds,
  matchedBoxIds,
  onDenied,
}: {
  revision: number;
  rosterActions: RosterActions;
  id: number;
  matchedIds: number[];
  matchedBoxIds: number[];
  onDenied: () => void;
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [data, setData] = useState<Details | null>(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Reset the selected server resource before loading its latest data.
    setError(false);
    void fetch(`/api/admin/help-desk/users/${id}?page=${page}`, {
      signal: controller.signal,
      credentials: 'same-origin',
      cache: 'no-store',
    })
      .then(async (response) => {
        if ([401, 403].includes(response.status)) {
          onDenied();
          return;
        }
        if (!response.ok) throw new Error('Unavailable');
        const result = await response.json();
        if (!result.user || !Array.isArray(result.cards)) throw new Error('Invalid result');
        if (!controller.signal.aborted) setData(result);
      })
      .catch(() => {
        if (!controller.signal.aborted) setError(true);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, page, retry, revision]);
  if (error)
    return (
      <Hint tone="error">
        {t('admin.helpDesk.error')}{' '}
        <Button onClick={() => setRetry((value) => value + 1)}>{t('admin.helpDesk.retry')}</Button>
      </Hint>
    );
  if (!data) return <Spinner label={t('account.loading')} />;
  const groups = new Map<number, Card[]>();
  data.cards.forEach((card) => groups.set(card.box_id, [...(groups.get(card.box_id) ?? []), card]));
  return (
    <div className="help-desk__detail" data-testid={`help-desk-user-${id}`}>
      {[...groups].map(([boxId, cards]) => (
        <GameRow rosterActions={rosterActions} currentUserId={id} key={boxId} cards={cards} matchedIds={matchedIds} matchedBoxIds={matchedBoxIds} onDenied={onDenied} onRevoked={() => setRetry(value => value + 1)} />
      ))}
      <Pager page={data.page} total={data.total} pageSize={data.pageSize} onChange={setPage} />
    </div>
  );
}
function Pager({
  page,
  total,
  pageSize,
  onChange,
}: {
  page: number;
  total: number;
  pageSize: number;
  onChange: (page: number) => void;
}) {
  const { t } = useTranslation();

  return <Pagination alwaysVisible page={page} pageCount={Math.ceil(total / pageSize)} onChange={onChange} previousLabel={t('admin.payments.previous')} nextLabel={t('admin.payments.next')} pageLabel={value => t('admin.payments.page', { page: value })} />;
}
export function AdminHelpDesk({ onDenied }: { onDenied: () => void }) {
  const { t, i18n } = useTranslation();
  const [input, setInput] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [result, setResult] = useState<Results | null>(null);
  const [premiumRevision, setPremiumRevision] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [checkedUsers, setCheckedUsers] = useState<number[]>([]);
  const checkedCount = checkedUsers.length;
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Reset the selected server resource before loading its latest data.
    setCheckedUsers(current => current.filter(id => result?.users.some(user => user.id === id)));
  }, [result]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const searchInput = useRef<HTMLInputElement>(null);
  const request = useRef<AbortController | null>(null);
  useEffect(() => () => request.current?.abort(), []);
  function clearSearch() {
    request.current?.abort();
    request.current = null;
    setInput('');
    setSubmitted('');
    setResult(null);
    setSelected(null);
    setCheckedUsers([]);
    setError(false);
    setLoading(false);
    searchInput.current?.focus();
  }
  function removeUserResult(id: number) {
    setResult((previous) => {
      if (!previous) return previous;
      const users = previous.users.filter((user) => user.id !== id);
      const matchedCards = previous.matchedCards.filter((card) => card.user_id !== id);
      return users.length || matchedCards.length ? { ...previous, users, matchedCards } : null;
    });
    setSelected((current) => (current === id ? null : current));
    searchInput.current?.focus();
  }
  async function search(query: string, page = 1) {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setError(false);
    setLoading(true);
    try {
      const response = await fetch('/api/admin/help-desk/search', {
        method: 'POST',
        credentials: 'same-origin',
        signal: controller.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, page }),
      });
      if ([401, 403].includes(response.status)) {
        onDenied();
        return;
      }
      if (!response.ok) throw new Error('Search unavailable');
      const data: Results = await response.json();
      if (!Array.isArray(data.users) || !Array.isArray(data.matchedCards))
        throw new Error('Invalid result');
      if (!controller.signal.aborted) {
        if (page === 1 && data.users.length > 0) {
          setInput((current) => (current.trim() === query ? '' : current));
        }
        setSubmitted(query);
        setResult((previous) => ({
          ...data,
          matchedBoxIds: [
            ...new Set([...(previous?.matchedBoxIds ?? []), ...(data.matchedBoxIds ?? [])]),
          ],
          users: Array.from(
            new Map(
              [...(previous?.users ?? []), ...data.users].map((user) => [user.id, user]),
            ).values(),
          ),
          matchedCards: Array.from(
            new Map(
              [...(previous?.matchedCards ?? []), ...data.matchedCards].map((card) => [
                card.id,
                card,
              ]),
            ).values(),
          ),
        }));
      }
    } catch {
      if (!controller.signal.aborted) setError(true);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }
  return (
    <section className="help-desk" aria-label={t('admin.tabs.support')}>
      <form
        className="help-desk__search"
        onSubmit={(event) => {
          event.preventDefault();
          if (input.trim()) void search(input.trim());
        }}
      >
        <label>
          {t('admin.helpDesk.search')}
          <input
            ref={searchInput}
            type="search"
            enterKeyHint="search"
            value={input}
            maxLength={2048}
            onChange={(event) => setInput(event.target.value)}
            placeholder={t('admin.helpDesk.placeholder')}
            aria-describedby="help-desk-search-mode"
          />
        </label>
        <Button type="button" variant="secondary" onClick={clearSearch}>
          {t('admin.helpDesk.clearSearch')}
        </Button>
      </form>
      <p id="help-desk-search-mode" className="help-desk__hint" aria-live="polite">
        {/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.trim())
          ? (i18n.language.startsWith('ru') ? 'Точный поиск по email' : 'Exact email lookup')
          : t('admin.helpDesk.hint')}
      </p>
      {loading && <Spinner label={t('account.loading')} />}
      {error && <Hint tone="error">{t('admin.helpDesk.error')}</Hint>}
      {result && (
        <>
          <p role="status">
            {t('admin.helpDesk.results', { count: result.users.length, query: submitted })}
          </p>
          {result.total === 0 && <p>{t('admin.helpDesk.noNewMatches')}</p>}
          {result.matchedCards.length > 0 && (
            <section>
              <h3>{t('admin.helpDesk.matchedCards')}</h3>
              {result.matchedCards.map((card) =>
                result.users.some((user) => user.id === card.user_id) ? (
                  <div key={card.id} className="help-desk__match">
                    <button type="button" onClick={() => setSelected(card.user_id)}>
                      {t('admin.helpDesk.card')} · {card.username || '—'}
                    </button>{' '}
                    <IdTag id={card.id} /> · {t('admin.helpDesk.userId')}{' '}
                    {card.user_id != null && <IdTag id={card.user_id} />} ·{' '}
                    {t('admin.helpDesk.boxId')} <IdTag id={card.box_id} />
                  </div>
                ) : (
                  <Button key={card.id} variant="secondary" icon={<EyeIcon />} onClick={() => showDialog({
                    title: <CardDialogTitle card={card} />, mobileFullScreen: true,
                    closeLabel: i18n.language.startsWith('ru') ? 'Закрыть' : 'Close', testId: 'help-desk-card-dialog',
                    children: <CardDialog id={card.id} onDenied={onDenied} />,
                  })}>{t('admin.helpDesk.card')} #{card.id} · {card.username || '—'}</Button>
                ),
              )}
            </section>
          )}
          {result.users.map((user) => (
            <article key={user.id} className={`help-desk__user${checkedUsers.includes(user.id) ? ' help-desk__user--checked' : ''}`}>
              <Button
                variant="ghost"
                size="small"
                className="help-desk__remove-result"
                icon={<CloseIcon />}
                aria-label={t('admin.helpDesk.removeResult', {
                  name: user.username || `#${user.id}`,
                })}
                title={t('admin.helpDesk.removeResult', { name: user.username || `#${user.id}` })}
                onClick={() => removeUserResult(user.id)}
              />
              <div className="help-desk__summary">
                <div
                  className={`help-desk__user-heading${
                    selected === user.id ? ' help-desk__user-heading--expanded' : ''
                  }`}
                >
                  <button
                    type="button"
                    className="help-desk__expand-user"
                    aria-label={`#${user.id} · ${user.username || t('admin.helpDesk.noName')}`}
                    aria-expanded={selected === user.id}
                    aria-controls={`help-desk-details-${user.id}`}
                    onClick={() => setSelected(selected === user.id ? null : user.id)}
                  >
                    <span aria-hidden="true">
                      <ChevronDownIcon />
                    </span>
                  </button>
                  <span className="help-desk__select-user">
                    <Checkbox fit checked={checkedUsers.includes(user.id)}
                      aria-label={i18n.language.startsWith('ru') ? `Выбрать пользователя #${user.id} · ${user.username || '—'}` : `Select user #${user.id} · ${user.username || '—'}`}
                      onCheckedChange={checked => setCheckedUsers(current => checked ? [...new Set([...current, user.id])] : current.filter(id => id !== user.id))} />
                  </span>
                  <span className="help-desk__avatar">
                    {user.avatar ? (
                      <img src={user.avatar} alt="" />
                    ) : (
                      <ParticipantFace picture={user.picture} index={user.id} size="identity" />
                    )}
                  </span>
                  <span className="help-desk__identity">
                    <span className="help-desk__names">
                      <span className="help-desk__name-id">
                        <IdTag id={user.id} />
                        <strong className="help-desk__username">
                          {user.username || t('admin.helpDesk.noName')}
                        </strong>
                        {user.active && <span className="help-desk__user-active"><span className="help-desk__user-active-icon" aria-hidden="true"><CheckIcon /></span>{i18n.language.startsWith('ru') ? 'Активен' : 'Active'}</span>}
                        {!user.active && (
                          <Badge className="help-desk__user-status help-desk__user-status--deleted">
                            {t('admin.helpDesk.deleted')}
                          </Badge>
                        )}
                        {user.principal_kind === 'guest' && (
                          <Badge tone="warning">{t('admin.helpDesk.guest')}</Badge>
                        )}
                      </span>
                      <span className="help-desk__email-status">
                        <span
                          className={`help-desk__email help-desk__email--${
                            user.is_verified ? 'verified' : 'unverified'
                          }`}
                        >
                          {user.email ? (
                            <CopyValue
                              value={user.email}
                              label={`${t('admin.helpDesk.copyEmail')} · ${t(
                                `admin.helpDesk.${user.is_verified ? 'verified' : 'unverified'}`,
                              )}`}
                            />
                          ) : (
                            t('admin.helpDesk.noEmail')
                          )}
                        </span>
                      </span>
                      <span className="help-desk__game-count">
                        {t('admin.helpDesk.games')}: <strong>{user.boxes}</strong>
                  {user.is_premium && (
                    <span className="help-desk__user-premium">
                      <PremiumBadge tier={user.premium_tier?.startsWith('tier_') ? user.premium_tier as PremiumTier : undefined}>
                        {user.premium_tier?.startsWith('tier_') ? premiumTierName(user.premium_tier as PremiumTier, i18n.language.startsWith('ru')) : user.premium_tier === 'custom' ? (i18n.language.startsWith('ru') ? 'Премиум · особый доступ' : 'Premium · custom access') : (i18n.language.startsWith('ru') ? 'Премиум · полный доступ' : 'Premium · full access')}
                      </PremiumBadge>
                    </span>
                  )}
                      </span>
                    </span>
                    <UserTimestamps user={user} />
                    <span className="help-desk__recovery-column"><RecoveryStatus saved={user.recovery_codes_saved_at} /></span>
                    <SocialAccounts user={user} />
                  </span>
                  {user.active && ['account', 'guest'].includes(user.principal_kind) && (
                    <section className="help-desk__actions" aria-label={i18n.language.startsWith('ru') ? 'Действия' : 'Actions'}>
                      <h3>{i18n.language.startsWith('ru') ? 'Действия' : 'Actions'}</h3>
                      <div className="help-desk__action-buttons">
                {user.active && ['account', 'guest'].includes(user.principal_kind) && <HelpDeskGift userId={user.id} />}
                {user.active && ['account', 'guest'].includes(user.principal_kind) && <HelpDeskAccessLink userId={user.id} />}
                {user.active && ['account', 'guest'].includes(user.principal_kind) && <HelpDeskPremium
                  userId={user.id} email={user.email} currentTier={user.premium_tier} onDenied={onDenied}
                  onGranted={tier => setResult(previous => previous && ({ ...previous, users: previous.users.map(item => item.id === user.id ? { ...item, is_premium: true, premium_tier: tier } : item) }))}
                />}
                <HelpDeskDeleteAccount id={user.id} name={user.username || user.email || String(user.id)} onDenied={onDenied}
                  onDeleted={() => {
                    setResult(previous => previous && ({ ...previous, users: previous.users.filter(item => item.id !== user.id), matchedCards: previous.matchedCards.filter(card => card.user_id !== user.id) }));
                    setSelected(null);
                  }} />
                <HelpDeskRevokePremium resource="users" id={user.id} onDenied={onDenied}
                  onRevoked={() => {
                    setResult(previous => previous && ({ ...previous, users: previous.users.map(item => item.id === user.id ? { ...item, is_premium: false, premium_tier: null } : item) }));
                    setPremiumRevision(value => value + 1);
                  }} />
                      </div>
                    </section>
                  )}

                </div>
              </div>
              <div id={`help-desk-details-${user.id}`}>
                {selected === user.id && (
                  <UserDetails
                    rosterActions={{ addUser: id => { void search(String(id)); }, userIds: result.users.map(item => item.id), busy: loading, refresh: () => setPremiumRevision(value => value + 1) }}
                    key={`${user.id}:${user.premium_tier}`}
                    revision={premiumRevision}
                    id={user.id}
                    matchedIds={result.matchedCards.map((card) => card.id)}
                    matchedBoxIds={result.matchedBoxIds ?? []}
                    onDenied={onDenied}
                  />
                )}
              </div>
            </article>
          ))}
          <Pager
            page={result.page}
            total={result.total}
            pageSize={result.pageSize}
            onChange={(page) => void search(submitted, page)}
          />
        </>
      )}
      {checkedCount > 0 && <div className="help-desk__selection-space">
        <section className="help-desk__selection-bar" aria-label={i18n.language.startsWith('ru') ? 'Действия с выбранными пользователями' : 'Selected user actions'}>
          <strong role="status">{i18n.language.startsWith('ru') ? `Выбрано пользователей: ${checkedCount}` : `Selected users: ${checkedCount}`}</strong>
          <Button variant="secondary" icon={<UsersIcon />} disabled={checkedCount < 2} onClick={() => showDialog({
            title: i18n.language.startsWith('ru') ? 'Объединить аккаунты' : 'Merge accounts',
            closeLabel: i18n.language.startsWith('ru') ? 'Закрыть' : 'Close',
            preventDismiss: true,
            hideClose: true,
            testId: 'help-desk-merge-accounts',
            children: <MergeAccountSelection users={(result?.users || []).filter(user => checkedUsers.includes(user.id))} onChanged={() => { setCheckedUsers([]); setSelected(null); setPremiumRevision(value => value + 1); void (async () => { for (const id of checkedUsers) await search(String(id)); })(); }} />,
          })}>{i18n.language.startsWith('ru') ? 'Объединить аккаунты' : 'Merge accounts'}</Button>
          <Button variant="ghost" onClick={() => setCheckedUsers([])}>{i18n.language.startsWith('ru') ? 'Снять выделение' : 'Clear selection'}</Button>
        </section>
      </div>}
    </section>
  );
}
